import { useDisplayOptionContext } from "@/hooks/useDisplayOptionContext";
import { ButtonLayout, QuoteType } from "@/types/enums";
import { useFacility } from "@/hooks/useFacility";
import { UnitGroup } from "@/types";
export const useTriggerButtonVisibility = (unitGroup: UnitGroup) => {
    const {
        buttonLayout,
        disableRental,
        disableReservation,
        disableSoftReservation,
    } = useDisplayOptionContext();

    const { facility } = useFacility(unitGroup.representative_unit.facility_id);

    const hasMultipleSubGroups =
        Array.isArray(unitGroup.sub_groups) && unitGroup.sub_groups.length > 1;
    const hasAvailableUnits = unitGroup.available > 0;
    const reservationWindowAllowed = unitGroup.reservation_allowed;

    /*
     * Rentals are enabled if:
     * - plugin doesn't disable rental
     * - not reserve-only
     * - not click-to-call
     */
    const canRent = !disableRental;

    /*
     * Reservations are enabled if:
     * - plugin doesn't disable reservation
     * - not click-to-call
     * - (window allows) OR (reserve-only overrides window=0)
     */
    const canReserve = !disableReservation && reservationWindowAllowed;

    /*
     * Soft reservations are enabled if:
     * - plugin doesn't disable soft reservations
     * - not click-to-call
     */
    const canSoftReserve =
        !disableSoftReservation &&
        facility?.settings.quote_type !== QuoteType.CALL;

    const shouldShowSelectButton = () => {
        /*
         * If the button layout is "separate", never show Select.
         */
        if (buttonLayout === ButtonLayout.SEPARATE) {
            return false;
        }

        /*
         * If we can't rent or reserve, we can't show the select button,
         * just 'call for availability', or 'get a quote'
         */
        if (!canRent && !canReserve) {
            return false;
        }

        /*
         * If there are no available units, the only button that will show
         * will be Quote or Call for Availability, never Select.
         */
        if (!hasAvailableUnits) {
            return false;
        }

        /*
         * If we reach here, we know:
         * - Button layout is 'combined'
         * - There are available units
         * - The unit can either be rented or reserved
         */
        return true;
    };

    return {
        hasMultipleSubGroups,
        hasAvailableUnits,
        canRent,
        canReserve,
        canSoftReserve,
        shouldShowSelectButton,
    };
};
